﻿import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { createStyles, makeStyles } from "@mui/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import CustomLoadingOverlay from "../../../../../../components/loaders/LazyLoading/LazyLoading";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	IContextMenuParams,
	MenuItemDef,
	RowClassParams,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WorkOrderLOTO } from "../../models/WorkOrderLOTO";
import { ErrorModal } from "@dexteel/mesf-core";
import { LOTOLinkButton } from "./components/link-button/LOTO-link-button.columns";
import { LOTONumberColumn } from "./components/editing-row/LOTO-LOTONumber.column";
import { LOTONameColumn } from "./components/editing-row/LOTO-LOTOName.column";
import { useSearchWorkOrderLOTO } from "./hooks/useSearchWorkOrderLOTO";
import { useWorkOrderLOTOGridActionsAndButtons } from "./hooks/useWorkOrderLOTOsGridActionsAndButtons";
import { AssetAddLOTODialog } from "../asset-resources-dialogs/LOTO-resource/asset-add-LOTO-dialog";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 5.5,
			right: 25,
		},
		btnDelete: {
			position: "absolute",
			padding: 0,
			bottom: 7,
			right: 63,
		},
		btnCached: {
			position: "absolute",
			padding: 0,
			bottom: 8,
			left: 5,
		},
	}),
);
type Props = {
	WorkOrderId: number | null;
	newMode?: boolean;
	AssetId: number | null;
	searchAssetResources: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShowAssetLOTOPicker: Function;
	isLoadingResourcesList: boolean;
	WorkOrderName: string;
	AssetName: string;
};

export const WorkOrderLOTOTable = ({
	WorkOrderId,
	newMode,
	AssetId,
	searchAssetResources,
	show,
	isLoadingResourcesList,
	WorkOrderName,
	AssetName,
}: Props) => {
	const classes = useStyles();
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");
	const isMountRef = useRef(true);

	const [workOrderLOTOError, setWorkOrderLOTOError] = useState<string>("");

	const [linkLOTOError, setLinkLOTOError] = useState<string>("");

	const [isWorkOrderLOTOLoading, setIsWorkOrderLOTOLoading] =
		useState<boolean>(false);
	const [removeLOTOError, setRemoveLOTOError] = useState<string>("");

	const [showLinkResourcesToAssetDialog, setShowLinkResourcesToAssetDialog] =
		useState<boolean>(false);

	const [listMessage, setListMessage] = useState<string>("");
	const [localShowInheritMessage, setLocalShowInheritMessage] = useState(false);

	const {
		state: { assetLOTO, workOrderLOTOList, rowSelectedInLOTO, isCreatingLOTO },
		actions: { setRowSelectedInLOTO, setWorkOrderLOTOList },
	} = useWorkOrderModalsContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listLOTOSelected, setListLOTOSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchWorkOrderLOTO = useSearchWorkOrderLOTO({
		WorkOrderId,
		setIsWorkOrderLOTOLoading,
		setWorkOrderLOTOError,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const getRowStyle = useCallback(
		(params: RowClassParams): { [key: string]: string } | undefined => {
			if (params.data.IsAssetResource) {
				return { backgroundColor: "#e8f5e9" };
			}
		},
		[],
	);

	const {
		refresh,
		saveRow,
		addRow,
		cancelAddLOTO,
		removeLOTO,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useWorkOrderLOTOGridActionsAndButtons({
		WorkOrderId,
		newMode,
		setShowLinkResourcesToAssetDialog,
		gridRef: gridApi,
		searchWorkOrderLOTO,
		setRemoveLOTOError,
		setListMessage,
	});

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 33,
			maxWidth: 33,
			filter: false,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
		});
		newColumns.push({
			cellRenderer: LOTONameColumn,
			field: "LOTOName",
			headerName: "LOTO Name",
			flex: 4,
			minWidth: 150,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: LOTONumberColumn,
			field: "LOTONumber",
			headerName: "LOTO ID",
			flex: 3,
			minWidth: 250,
			maxWidth: 300,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: LOTOLinkButton,
			field: "LOTO view",
			headerName: "LOTO",
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			type: "columnProperties",
		});
		newColumns.push({
			headerName: "",
			flex: 3,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setRowSelectedInLOTO(params.node?.data);
			const rowSelectedInTable = params.node?.data;
			const selectedLOTO = gridApi?.getSelectedNodes();

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (deleteButton && selectedLOTO?.length) {
				result.push({
					name: "Remove LOTO Link",
					action: () => {
						setRowSelectedInLOTO(params.node?.data);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (
				!selectedLOTO?.length &&
				rowSelectedInTable?.id !== -1 &&
				rowSelectedInTable
			) {
				result.push({
					name: "Remove LOTO Link",
					action: () => {
						setRowSelectedInLOTO(params.node?.data);
						setListLOTOSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInTable) {
				result.push("separator");
			}

			if (!saveButton) {
				result.push({
					name: "Add LOTO Link",
					action: addRow,
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return result;
		},
		[deleteButton, saveButton, addRow, WorkOrderId, gridApi, rowSelectedInLOTO],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listLOTOSelected = event.api.getSelectedNodes();
		setListLOTOSelected(listLOTOSelected);
		rowCount > 0 ? setDeleteButton(true) : setDeleteButton(false);
	}, []);

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
	}, [WorkOrderId]);

	useEffect(() => {
		if (!isCreatingLOTO) cancelAddLOTO();
	}, [isCreatingLOTO]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		const hasAssetResource = workOrderLOTOList.some(
			(LOTO: WorkOrderLOTO) => LOTO.IsAssetResource === true,
		);
		const shouldShowInheritMessage =
			(!hasAssetResource || workOrderLOTOList.length === 0) &&
			assetLOTO.length > 0;

		if (show && newMode && shouldShowInheritMessage && isMountRef.current) {
			setLocalShowInheritMessage(true);
			isMountRef.current = false;
		} else if (!show) {
			setLocalShowInheritMessage(false);
		}
	}, [assetLOTO, show]);

	return (
		<>
			{show && (
				<div style={containerStyle}>
					<Grid2 container style={gridStyle}>
						<Grid2
							style={{ paddingBottom: 45 }}
							className={classes.agGridContainer}
							size={{ md: 12, xs: 12 }}
						>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={workOrderLOTOList}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								getRowStyle={getRowStyle}
								overlayNoRowsTemplate={"No LOTO added"}
								loading={isWorkOrderLOTOLoading}
							/>
							{addButton && (
								<>
									<IconButton
										onClick={addRow}
										aria-label="add"
										color="primary"
										className={classes.btnAdd}
									>
										<AddBoxIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
							{saveButton && discardButton && (
								<>
									<IconButton
										onClick={() => saveRow()}
										aria-label="save"
										className={classes.btnAdd}
										color="primary"
									>
										<SaveIcon style={{ width: 36, height: 36 }} />
									</IconButton>
									<IconButton
										onClick={cancelAddLOTO}
										aria-label="cancel"
										className={classes.btnDelete}
										color="inherit"
									>
										<CancelIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
						</Grid2>
						<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
							<DialogTitle id="responsive-dialog-title">
								{"WARNING"}
							</DialogTitle>
							<DialogContent style={{ minWidth: 400 }}>
								<DialogContentText style={{ padding: 0, margin: 0 }}>
									{listLOTOSelected?.length === 1 ? (
										<>
											<p style={{ margin: "0" }}>
												REMOVE <b>{listLOTOSelected[0].data.LOTOName}</b> from
												work order?
											</p>
										</>
									) : listLOTOSelected.length > 1 ? (
										<>
											<p style={{ margin: 0 }}> REMOVE:</p>
											{listLOTOSelected?.map((LOTO) => {
												return (
													<>
														<p style={{ margin: "5px 0" }}>
															<b>⁃ {LOTO.data.LOTOName}, </b>
														</p>
													</>
												);
											})}
											from work order?
										</>
									) : rowSelectedInLOTO?.LOTOName &&
										listLOTOSelected?.length === 0 ? (
										<>
											<p style={{ margin: "0" }}>
												REMOVE <b>{rowSelectedInLOTO?.LOTOName}</b> from work
												order?
											</p>
										</>
									) : (
										<></>
									)}
								</DialogContentText>
							</DialogContent>
							<DialogActions style={{ padding: "16px 24px" }}>
								<Button
									onClick={() => setDeleteOpen(false)}
									autoFocus
									color="inherit"
								>
									CANCEL
								</Button>
								<Button
									autoFocus
									color="secondary"
									onClick={() => {
										if (listLOTOSelected.length > 0) {
											removeLOTO(listLOTOSelected);
										} else if (rowSelectedInLOTO) {
											removeLOTO([rowSelectedInLOTO]);
										}
									}}
								>
									REMOVE
								</Button>
							</DialogActions>
						</Dialog>
						<AssetAddLOTODialog
							show={showLinkResourcesToAssetDialog}
							onHide={(shouldUpdate: boolean) => {
								if (shouldUpdate) {
									searchWorkOrderLOTO();
								}
								setShowLinkResourcesToAssetDialog(false);
							}}
							AssetId={AssetId as number}
							searchAssetResources={searchAssetResources}
							setRemoveLOTOError={setRemoveLOTOError}
							AssetName={AssetName as string}
							WorkOrderName={WorkOrderName as string}
						/>
					</Grid2>
					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
					<ErrorModal
						error={workOrderLOTOError}
						onHide={() => setWorkOrderLOTOError("")}
					/>
					<ErrorModal
						error={linkLOTOError}
						onHide={() => setLinkLOTOError("")}
					/>
					<ErrorModal
						error={removeLOTOError}
						onHide={() => setRemoveLOTOError("")}
					/>
					<ErrorModal error={listMessage} onHide={() => setListMessage("")} />
				</div>
			)}
		</>
	);
};
